import { ActionIcon, Tooltip } from '@mantine/core';
import { type CameraControls, Environment } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { variants } from '@uag/react-core';
import { memo, Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxHelper, type WebGLRenderer } from 'three';
import { useShallow } from 'zustand/shallow';

import { InsertPointsVisibiltyButton } from 'app/modules/wallforming/components/catalogue-container/article-preview/InsertPointsVisibilityButton';
import { ArticlePreviewScene } from 'app/modules/wallforming/components/scenes/ArticlePreviewScene';
import { PreviewCameraControls } from 'app/modules/wallforming/components/scenes/camera-controls/PreviewCameraControls';
import { PreviewOrientationCube } from 'app/modules/wallforming/components/scenes/camera-controls/PreviewOrientationCube';
import { isFramiClamp } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { handleOnInsertionStart } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { SceneLighting } from 'app/modules/wallforming/components/scenes/SceneLighting';
import { ScenePostProcessing } from 'app/modules/wallforming/components/scenes/ScenePostProcessing';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const screenWidth = window.screen.width;
const TIMEOUT_DELAY_MS = 300;

export const CatalogueArticlePreview = memo(() => {
    const { t } = useTranslation();

    const { fitToObject, isArticleInsertionEnabled, setIsDragNewArticleStartRef, selectedArticleId } =
        usePlacementStore(
            useShallow((state) => ({
                fitToObject: state.fitToObject,
                isArticleInsertionEnabled: state.isArticleInsertionEnabled,
                setIsDragNewArticleStartRef: state.setIsDragNewArticleStartRef,
                selectedArticleId: state.selectedArticleId
            }))
        );

    const { shadowsEnabled, reflectionsEnabled } = usePlacementStore(useShallow((state) => state.graphicsSettings));

    const previewHelperRef = useRef<BoxHelper | undefined>(undefined);
    const previewCameraControlsRef = useRef<CameraControls | null>(null);
    const webGLRendererRef = useRef<WebGLRenderer | null>(null);

    const wheelClickCountRef = useRef<number>(0);
    const wheelClickTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    const handleFocusArticle = () => {
        if (previewHelperRef.current && previewCameraControlsRef.current) {
            fitToObject(previewHelperRef.current, previewCameraControlsRef, true, false);
        }
    };

    const handleOnDragStart = (dragEvent: React.DragEvent): void => {
        if ((dragEvent.buttons & 1) === 0) {
            return;
        }

        handleOnInsertionStart(false, selectedArticleId, t);
        dragEvent.preventDefault();
    };

    const handleOnPointerDown = (pointerEvent: React.PointerEvent): void => {
        if (pointerEvent.button === 0 && selectedArticleId) {
            const articleNumber = usePlacementStore.getState().articles[selectedArticleId]?.articleNumber;
            if (!isFramiClamp(articleNumber)) {
                setIsDragNewArticleStartRef(true);
            }
        }

        if (pointerEvent.button === 1) {
            wheelClickCountRef.current++;

            if (wheelClickCountRef.current === 2) {
                handleFocusArticle();
                clearTimeout(wheelClickTimerRef.current);
                wheelClickCountRef.current = 0;
            } else {
                wheelClickTimerRef.current = setTimeout(() => {
                    wheelClickCountRef.current = 0;
                }, TIMEOUT_DELAY_MS);
            }
        }

        pointerEvent.stopPropagation();
    };

    const handleOnDoubleClick = (mouseEvent: React.MouseEvent): void => {
        if (mouseEvent.button !== 0) {
            return;
        }

        handleOnInsertionStart(true, selectedArticleId, t);
    };

    const handleOnPointerEnter = () => {
        if (!webGLRendererRef.current) {
            return;
        }
        const cursorStyle = isArticleInsertionEnabled() ? 'move' : 'auto';
        webGLRendererRef.current.domElement.style.setProperty('cursor', cursorStyle);
    };

    return (
        <div className="flex flex-col h-full justify-center items-center border-b-0">
            <div
                className="h-full w-full border border-border-ultra-light rounded-none overflow-hidden focus:outline-none"
                draggable="true"
                id="previewcanvas"
                tabIndex={0}
                onContextMenu={(e) => e.preventDefault()}
                onDoubleClick={handleOnDoubleClick}
                onDragStart={handleOnDragStart}
                onPointerDown={handleOnPointerDown}
                onPointerEnter={handleOnPointerEnter}
            >
                <>
                    <div className="relative">
                        <div className="absolute top-3 left-3 z-50">
                            <div
                                className="flex flex-col justify-start items-start gap-y-2"
                                onDoubleClick={(e) => e.stopPropagation()}
                            >
                                <Tooltip
                                    label={t('buttonFocusArticle')}
                                    position="right"
                                    variant={variants.tooltip.right}
                                >
                                    <ActionIcon
                                        className={`
                                        bg-background-gray-ultra-light hover:bg-background-gray! catalogue-preview-action-icon
                                        ${screenWidth >= 1920 ? 'action-icon-large' : 'action-icon-medium'}
                                    `}
                                        onClick={handleFocusArticle}
                                    >
                                        view_in_ar
                                    </ActionIcon>
                                </Tooltip>
                                <InsertPointsVisibiltyButton />
                            </div>
                        </div>
                    </div>
                    <Canvas
                        frameloop="demand"
                        shadows={shadowsEnabled ? 'soft' : false}
                        orthographic
                        onCreated={(state) => {
                            webGLRendererRef.current = state.gl;
                        }}
                    >
                        <PreviewCameraControls previewCameraControlsRef={previewCameraControlsRef} />
                        <PreviewOrientationCube />

                        <ArticlePreviewScene
                            previewCameraControlsRef={previewCameraControlsRef}
                            previewHelperRef={previewHelperRef}
                        />

                        <Suspense>
                            <Environment
                                background={false}
                                environmentIntensity={shadowsEnabled || reflectionsEnabled ? 1.6 : 0}
                                environmentRotation={[Math.PI / 2, -1, 0]}
                                preset="city"
                            />
                        </Suspense>

                        <SceneLighting />
                        <ScenePostProcessing />
                    </Canvas>
                </>
            </div>
        </div>
    );
});

CatalogueArticlePreview.displayName = 'CatalogueArticlePreview';
